import dynamic from 'next/dynamic'

import { CommentListPlaceProvider } from '@/components/editor-v3/DocxViewer/commentListPlace'
import {
  type CommentsPaused,
  CommentsPausedProvider,
} from '@/components/editor-v3/DocxViewer/commentsPaused'
import type { DocxViewerProps as DocxViewerImplProps } from '@/components/editor-v3/DocxViewer/DocxViewerImpl'
import DocumentLoadProgress, {
  LOAD_LABELS,
} from '@/components/editor-v3/ui/DocumentLoadProgress'

export type { CommentsPaused } from '@/components/editor-v3/DocxViewer/commentsPaused'

export type {
  DocumentNavigationHandle,
  DocxSelectionReveal,
  DocxViewerHandle,
  DocxViewerPageState,
} from '@/components/editor-v3/DocxViewer/DocxViewerImpl'

export interface DocxViewerProps extends DocxViewerImplProps {
  /**
   * Hold back new comments, saying why and where to leave one instead. The
   * document keeps everything else it was opened with, this one included: what
   * the reader has already written stays on screen and stays saveable.
   */
  commentsPaused?: CommentsPaused | null
}

// SuperDoc bundles Vue 3, Pinia, and Konva — none of which survive SSR.
const DocxViewerImpl = dynamic(
  () => import('@/components/editor-v3/DocxViewer/DocxViewerImpl'),
  {
    ssr: false,
    // Wearing the canvas classes the viewer itself does, so the chunk's own
    // wait is on the same ground as every wait after it.
    loading: () => (
      <div className="editor-v3-canvas editor-v3-viewer h-full w-full">
        <DocumentLoadProgress label={LOAD_LABELS.opening} />
      </div>
    ),
  },
)

/**
 * A Word document, rendered for reading — no chrome, no room, no document
 * record behind it. See DocxViewerImpl for what it does and why.
 */
export const DocxViewer: React.FC<DocxViewerProps> = ({
  commentsPaused = null,
  ...props
}) => {
  return (
    <CommentListPlaceProvider>
      <CommentsPausedProvider paused={commentsPaused}>
        <DocxViewerImpl {...props} />
      </CommentsPausedProvider>
    </CommentListPlaceProvider>
  )
}
